import { ConfiguratorState } from '../types';
import type { ProductKind } from '../components/UI/PerspectiveViewer';
import { getFixedMinDimensions } from '../config/priceConfigFixed';
import { getComfortMinDimensions } from '../config/priceConfigComfort';
import { getWalkOnMinDimensions } from '../config/priceConfigWalkOn';
import { getAccessMinDimensions } from '../config/priceConfigAccess';
import { isProductEnabled, getProductDisplayOrder } from '../config/productConfig';

export interface CatalogProductBase {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  startingPrice?: number | null;
  info?: {
    roofPitch?: string;
    optimalPitch?: string;
    general?: string;
  };
}

export interface CatalogFixedProduct extends CatalogProductBase {
  dimensions: {
    minWidth: number;
    maxWidth: number;
    minLength: number;
    maxLength: number;
    maxArea?: number | null;
    roofPitch: { min: number; max: number; optimal: number };
  };
}

export interface CatalogCircularProduct extends CatalogProductBase {
  dimensions: {
    minDiameter: number;
    maxDiameter: number;
    roofPitch: { min: number; max: number };
  };
}

export interface CatalogLinearProduct extends CatalogProductBase {
  isModular: true;
  moduleSize: { width: number; length: number };
  roofPitch: { min: number; max: number; optimal: number };
}

export type CatalogProduct =
  | CatalogFixedProduct
  | CatalogCircularProduct
  | CatalogLinearProduct;

export type ProductFamily =
  | 'fixed'
  | 'comfort'
  | 'circular'
  | 'walkon'
  | 'circularWalkon'
  | 'linear'
  | 'access'
  | 'linearWalkon';

export function productFamily(product: Pick<CatalogProduct, 'id' | 'name'>): ProductFamily {
  const n = `${product.id} ${product.name}`.toLowerCase();
  const walkable = n.includes('walkon') || n.includes('walk-on');
  if (n.includes('linear') && walkable) return 'linearWalkon';
  if (n.includes('circular') && walkable) return 'circularWalkon';
  if (walkable) return 'walkon';
  if (n.includes('circular')) return 'circular';
  if (n.includes('linear')) return 'linear';
  if (n.includes('access')) return 'access';
  if (n.includes('comfort')) return 'comfort';
  return 'fixed';
}

export function isWalkableProduct(product: string): boolean {
  const n = product.toLowerCase();
  return n.includes('walkon') || n.includes('walk-on');
}

export function isCircularProduct(product: string): boolean {
  return product.toLowerCase().includes('circular');
}

export function catalogProductKind(product: Pick<CatalogProduct, 'id' | 'name'>): ProductKind {
  const family = productFamily(product);
  if (family === 'circular' || family === 'circularWalkon') return 'circular';
  if (family === 'linear' || family === 'linearWalkon') return 'linear';
  if (family === 'access') return 'access';
  if (family === 'comfort') return 'comfort';
  return 'fixed';
}

export function catalogShortDescription(product: Pick<CatalogProduct, 'id' | 'name'>): string {
  switch (productFamily(product)) {
    case 'walkon':
      return 'Begehbares Flachdachfenster mit TG-Step';
    case 'circularWalkon':
      return 'Begehbares rundes Oberlicht mit TG-Step';
    case 'comfort':
      return 'Elektrisch öffenbar, Motor + Regensensor';
    case 'circular':
      return 'Rundes Flachdachfenster';
    case 'linear':
      return 'Modulares Bandfenster (1 m Raster)';
    case 'linearWalkon':
      return 'Begehbares Lichtband, modular';
    case 'access':
      return 'Ausstiegsfenster, Öffnung bis 75°';
    default:
      return 'Festverglastes Flachdachfenster';
  }
}

export function catalogRangeLabel(product: CatalogProduct): string {
  if ('isModular' in product) {
    return `${product.moduleSize.width} × ${product.moduleSize.length} mm / Modul`;
  }
  if ('minDiameter' in product.dimensions) {
    const d = product.dimensions;
    return `Ø ${d.minDiameter}–${d.maxDiameter} mm`;
  }
  const d = product.dimensions;
  return `${d.minWidth}–${d.maxWidth} × ${d.minLength}–${d.maxLength} mm`;
}

export function catalogPreviewDimensions(product: CatalogProduct) {
  if ('isModular' in product) {
    return { width: 3000, length: product.moduleSize.length, height: 210, modules: 3 };
  }
  if ('minDiameter' in product.dimensions) {
    return { diameter: 1200, width: 1200, length: 1200, height: 240 };
  }
  if (productFamily(product) === 'access') {
    return { width: 1000, length: 1500, height: 291 };
  }
  return { width: 1200, length: 1200, height: 210 };
}

export function filterCatalogProducts(
  products: CatalogProduct[],
  configProducts?: string,
): CatalogProduct[] {
  const filtered =
    !configProducts || configProducts === 'all'
      ? products
      : products.filter((product) => {
          const allowed = configProducts.split(',').map((p) => p.trim().toLowerCase());
          const type = product.name.toLowerCase();
          if (allowed.includes('fixed') && type.includes('fixed')) return true;
          if (allowed.includes('comfort') && type.includes('comfort')) return true;
          if (
            allowed.includes('circular-walkon') &&
            type.includes('circular') &&
            type.includes('walk')
          ) {
            return true;
          }
          if (allowed.includes('circular') && type.includes('circular') && !type.includes('walk')) {
            return true;
          }
          if (allowed.includes('linear-walkon') && type.includes('linear') && type.includes('walk')) {
            return true;
          }
          if (allowed.includes('linear') && type.includes('linear') && !type.includes('walk')) {
            return true;
          }
          if (
            allowed.includes('walkon') &&
            (type.includes('walkon') || type.includes('walk-on')) &&
            !type.includes('linear') &&
            !type.includes('circular')
          ) {
            return true;
          }
          if (allowed.includes('access') && type.includes('access')) return true;
          return false;
        });

  return filtered
    .filter((product) => isProductEnabled(product.id))
    .sort((a, b) => getProductDisplayOrder(a.id) - getProductDisplayOrder(b.id));
}

export function stateUpdatesForProduct(
  product: CatalogProduct,
): Partial<ConfiguratorState> {
  if ('isModular' in product) {
    const walkOnBand = productFamily(product) === 'linearWalkon';
    return {
      product: product.name,
      dimensions: {
        width: product.moduleSize.width,
        length: product.moduleSize.length,
        height: 210,
        diameter: 0,
      },
      modules: { fixed: 1, opening: 0 },
      glassType: walkOnBand ? 'double' : '',
      ralColor: 'RAL 7043',
      ralColorInside: walkOnBand ? 'RAL 9010' : undefined,
      accessories: [],
      totalPrice: 0,
    };
  }

  if ('minDiameter' in product.dimensions) {
    return {
      product: product.name,
      dimensions: {
        width: product.dimensions.minDiameter,
        length: product.dimensions.minDiameter,
        height: 240,
        diameter: product.dimensions.minDiameter,
      },
      modules: { fixed: 0, opening: 0 },
      glassType: '',
      ralColor: 'RAL 7043',
      accessories: [],
      totalPrice: 0,
    };
  }

  if (product.id === 'skyvision-comfort') {
    const baseDims = getComfortMinDimensions();
    return {
      product: product.name,
      dimensions: {
        width: baseDims.width,
        length: baseDims.length,
        height: baseDims.height,
        diameter: 0,
      },
      modules: { fixed: 0, opening: 0 },
      glassType: 'double',
      ralColor: 'RAL 7043',
      ralColorInside: 'RAL 9010',
      accessories: [],
      totalPrice: 0,
    };
  }

  if (product.id === 'skyvision-access') {
    const baseDims = getAccessMinDimensions();
    return {
      product: product.name,
      dimensions: {
        width: baseDims.width,
        length: baseDims.length,
        height: baseDims.height,
        diameter: 0,
      },
      modules: { fixed: 0, opening: 0 },
      glassType: 'double',
      ralColor: 'RAL 7043',
      ralColorInside: 'RAL 9010',
      accessories: [],
      controlType: 'wcc106',
      totalPrice: 0,
    };
  }

  if (product.id === 'skyvision-walkon') {
    const baseDims = getWalkOnMinDimensions();
    const dims = product.dimensions;
    return {
      product: product.name,
      dimensions: {
        width: dims.minWidth || baseDims.width,
        length: dims.minLength || baseDims.length,
        height: baseDims.height,
        diameter: 0,
      },
      modules: { fixed: 0, opening: 0 },
      glassType: 'double',
      ralColor: 'RAL 7043',
      ralColorInside: 'RAL 9010',
      accessories: [],
      totalPrice: 0,
    };
  }

  const baseDims = getFixedMinDimensions();
  return {
    product: product.name,
    dimensions: {
      width: baseDims.width,
      length: baseDims.length,
      height: baseDims.height,
      diameter: 0,
    },
    modules: { fixed: 0, opening: 0 },
    glassType: 'double',
    ralColor: 'RAL 7043',
    ralColorInside: 'RAL 9010',
    accessories: [],
    totalPrice: 0,
  };
}
